// src/pages/PortfolioPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PortfolioPage.module.css';
import { PortfolioService } from '../services/portfolio.service';
import type { PortfolioResponse, PortfolioPosition } from '../types/Portfolio';
import { computeAvailableFundsFromPositions } from '../utils/availableFunds';

function toISODate(d: Date) {
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'estado-de-cuenta';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function isValidRange(from: string, to: string) {
  if (!!from !== !!to) return false;
  if (!from && !to) return true;
  return new Date(from).getTime() <= new Date(to).getTime();
}

export default function PortfolioPage() {
  const { t, i18n } = useTranslation();
  const isSpanish = i18n.language.startsWith('es');
  const locale = isSpanish ? 'es-MX' : 'en-US';
  const currency = isSpanish ? 'MXN' : 'USD';

  const [data, setData] = useState<PortfolioResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return toISODate(d);
  });
  const [to, setTo] = useState(() => toISODate(new Date()));

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const formatCurrency = (amount: number) =>
    Number(amount || 0).toLocaleString(locale, { style: 'currency', currency, minimumFractionDigits: 0 });

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    PortfolioService.getMyPortfolio()
      .then(res => {
        if (!alive) return;
        setData(res || null);
      })
      .catch(() => {
        if (!alive) return;
        setData(null);
        setError(t('portfolio.load_error') || 'No se pudo cargar tu portafolio.');
      })
      .finally(() => {
        if (!alive) return;
        setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [t]);

  const positions: PortfolioPosition[] = useMemo(() => data?.positions || [], [data]);
  const available = useMemo(() => computeAvailableFundsFromPositions(positions), [positions]);
  const closedPositionsCount = useMemo(() => positions.filter(p => p.status === 'closed').length, [positions]);
  const statusLabels = useMemo(
    () => ({
      active: t('portfolio.status_active') || 'En ejecución',
      closed: t('portfolio.status_closed') || 'Finalizado',
      pending: t('portfolio.status_pending') || 'Pendiente'
    }),
    [t]
  );

  const onDownloadStatement = async (format: 'pdf' | 'csv') => {
    setDownloadError(null);

    // ✅ validación simple de rango
    if (!isValidRange(from, to)) {
      setDownloadError(t('portfolio.statement_invalid_range') || 'Rango de fechas inválido.');
      return;
    }

    setDownloading(true);
    try {
      const res = await PortfolioService.downloadAccountStatement({
        from: from || undefined,
        to: to || undefined,
        format
      });
      downloadBlob(res.blob, res.filename);
    } catch {
      setDownloadError(t('portfolio.statement_download_error') || 'No se pudo descargar el estado de cuenta.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <div className={styles.loading}>{t('common.loading') || 'Cargando...'}</div>;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('portfolio.title') || 'Portafolio'}</h1>
        <p className={styles.subtitle}>{t('portfolio.subtitle') || 'Resumen de tus posiciones e inversiones.'}</p>
      </header>

      {error ? <div className={styles.error}>{error}</div> : null}

      <section className={styles.statsGrid} aria-label={t('portfolio.title') || 'Portafolio'}>
        <article className={styles.statCard}>
          <div className={styles.statLabel}>{t('portfolio.total_invested') || 'Total invertido'}</div>
          <div className={styles.statValue}>{formatCurrency(data?.summary?.totalInvested || 0)}</div>
        </article>

        <article className={styles.statCard}>
          <div className={styles.statLabel}>{t('portfolio.expected_return') || 'Retorno estimado'}</div>
          <div className={styles.statValue}>{formatCurrency(data?.summary?.expectedReturn || 0)}</div>
        </article>

        <article className={styles.statCard}>
          <div className={styles.statLabel}>{t('portfolio.active_positions') || 'Posiciones activas'}</div>
          <div className={styles.statValue}>{String(data?.summary?.activePositions || 0)}</div>
        </article>

        <article className={styles.statCard}>
          <div className={styles.statLabel}>{t('portfolio.available_funds') || 'Fondos disponibles'}</div>
          <div className={styles.statValue}>{formatCurrency(available.total)}</div>
        </article>

        <article className={styles.statCard}>
          <div className={styles.statLabel}>{t('portfolio.closed_positions') || 'Posiciones cerradas'}</div>
          <div className={styles.statValue}>{String(closedPositionsCount)}</div>
        </article>
      </section>

      <section className={styles.statementCard}>
        <div className={styles.statementHeader}>
          <div className={styles.statementTitle}>{t('portfolio.statement_title') || 'Estado de cuenta'}</div>
          <div className={styles.statementHint}>
            {t('portfolio.statement_hint') || 'Descarga tu estado de cuenta en PDF o CSV para el periodo seleccionado.'}
          </div>
        </div>

        <div className={styles.statementBody}>
          <div className={styles.filters}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('common.from') || 'Desde'}</span>
              <input
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                className={styles.input}
                disabled={downloading}
              />
            </label>

            <label className={styles.field}>
              <span className={styles.fieldLabel}>{t('common.to') || 'Hasta'}</span>
              <input
                type="date"
                value={to}
                onChange={e => setTo(e.target.value)}
                className={styles.input}
                disabled={downloading}
              />
            </label>

            <button
              type="button"
              className={styles.primaryBtn}
              onClick={() => onDownloadStatement('pdf')}
              disabled={downloading}
            >
              {downloading ? t('common.loading') || 'Cargando...' : t('portfolio.download_statement') || 'Descargar PDF'}
            </button>

            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => onDownloadStatement('csv')}
              disabled={downloading}
            >
              {downloading
                ? t('common.loading') || 'Cargando...'
                : t('portfolio.download_statement_csv') || 'Descargar CSV'}
            </button>
          </div>

          {downloadError ? <div className={styles.inlineError}>{downloadError}</div> : null}
        </div>
      </section>

      <section className={styles.card}>
        <div className={styles.cardHeader}>
          <div className={styles.cardTitle}>{t('portfolio.positions') || 'Posiciones'}</div>
          <div className={styles.countBadge}>{String(positions.length)}</div>
        </div>

        <div className={styles.cardBody}>
          {positions.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>{t('portfolio.no_positions') || 'Aún no tienes posiciones.'}</div>
              <div className={styles.emptyText}>
                {t('portfolio.no_positions_hint') || 'Cuando inviertas, aquí verás tu resumen.'}
              </div>
            </div>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>{t('portfolio.table_project') || 'Proyecto'}</th>
                    <th>{t('portfolio.table_invested') || 'Invertido'}</th>
                    <th>{t('portfolio.table_expected') || 'Retorno'}</th>
                    <th>{t('portfolio.table_status') || 'Estado'}</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map(p => (
                    <tr key={`${p.projectId}-${p.status}`}>
                      <td className={styles.tdProject}>
                        <div className={styles.projectTitle}>{p.projectTitle}</div>
                        <div className={styles.projectMeta}>#{p.projectId}</div>
                      </td>
                      <td className={styles.mono}>{formatCurrency(p.investedAmount)}</td>
                      <td className={styles.tdReturn}>
                        <div className={styles.mono}>{formatCurrency(p.expectedReturn)}</div>
                        {p.status !== 'closed' ? (
                          <div className={styles.returnHint}>
                            {t('portfolio.return_pending') || 'Se calcula al finalizar el proyecto.'}
                          </div>
                        ) : null}
                      </td>
                      <td>
                        <span className={styles.statusChip}>
                          {statusLabels[p.status] || p.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
